<template>
  <div>
    <h1 class="main-header" v-translate translate-context="customer_success.search">Search Users</h1>

    <!-- Go to User ID / Pica Code -->
    <CCard class="mb-3">
      <CCardHeader class="py-2">
        <CIcon name="cipArrowNarrowRight" size="sm" class="mr-2"/>
        <translate translate-context="customer_success.search">Go to</translate>
      </CCardHeader>
      <CCardBody class="py-2">
        <div class="d-flex flex-wrap goto-row">
          <div class="goto-item">
            <label class="">User ID</label>
            <div class="input-with-btn">
              <CInput
                v-model="form.user_id"
                type="number"
                size="sm"
                class="mb-0"
                placeholder="123456"
                @keyup.enter="goToUserId"
              />
              <CButton
                color="primary"
                size="sm"
                class="btn-circle btn-circle--sm"
                :disabled="!form.user_id"
                @click="goToUserId"
              >
                <CIcon name="cipArrowNarrowRight" size="sm"/>
              </CButton>
            </div>
          </div>
          <div class="goto-item">
            <label class="mb-1">Pica Code</label>
            <div class="input-with-btn">
              <CInput
                v-model="form.pica_code"
                size="sm"
                placeholder="ABC123"
                class="mb-0"
                @keyup.enter="goToPicaCode"
              />
              <CButton
                color="primary"
                size="sm"
                  class="btn-circle btn-circle--sm"
                :disabled="!form.pica_code"
                @click="goToPicaCode"
              >
                <CIcon name="cipArrowNarrowRight" size="sm"/>
              </CButton>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Search by Email -->
    <CCard class="mb-4">
      <CCardHeader class="py-2">
        <CIcon name="cil-search" size="sm" class="mr-2"/>
        <translate translate-context="customer_success.search">Search by Email</translate>
      </CCardHeader>
      <CCardBody class="py-3">
        <CInput
          v-model="form.email"
          type="email"
          placeholder="user@example.com"
          @input="onEmailInput"
        />
        <small class="text-muted">
          <translate translate-context="customer_success.search">Start typing to search (min 3 characters)</translate>
        </small>
      </CCardBody>
    </CCard>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <CSpinner size="sm" class="mr-2"/>
      <span class="text-muted">
        <translate translate-context="customer_success.search">Searching...</translate>
      </span>
    </div>

    <!-- Results -->
    <CCard v-else-if="users.length > 0">
      <CCardHeader class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <CIcon name="cipUser01" class="mr-2"/>
          <translate translate-context="customer_success.search">Results</translate>
          <CBadge color="primary" class="ml-2">{{ users.length }}</CBadge>
        </div>
        <span v-if="users.length >= 20" class="text-warning small">
          <CIcon name="cipAlertTriangle" size="sm" class="mr-1"/>
          <translate translate-context="customer_success.search">Too many results, please refine your search</translate>
        </span>
      </CCardHeader>
      <CCardBody class="p-0">
        <div class="user-list">
          <div
            v-for="user in users"
            :key="user.id"
            class="user-item"
            @click="openUser(user)"
          >
            <div class="user-main">
              <div class="user-info">
                <span class="user-email">{{ user.email }}</span>
                <span class="user-id text-muted">ID: {{ user.id }}</span>
              </div>
              <div class="user-actions">
                <div class="user-badges mr-2">
                  <CBadge v-if="user.is_virtual" color="warning" class="badge-outline mr-1">Virtual</CBadge>
                  <CBadge v-if="user.is_active" color="success" class="badge-outline">Active</CBadge>
                  <CBadge v-else color="danger" class="badge-outline">Inactive</CBadge>
                </div>
                <CButton
                  v-if="canImpersonate"
                  color="warning"
                  size="sm"
                  variant="outline"
                  @click.stop="onImpersonate(user)"
                >
                  <CIcon name="cipUser" size="sm" class="mr-1"/>
                  <translate translate-context="customer_success.search">Impersonate</translate>
                </CButton>

              </div>
            </div>

            <!-- Checkins preview -->
            <div v-if="user.checkins && user.checkins.length > 0" class="user-checkins">
              <span class="text-muted small">
                {{ user.checkins.length }} checkin{{ user.checkins.length > 1 ? 's' : '' }}
              </span>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- No results message -->
    <div v-else-if="hasSearched && !loading" class="text-center py-4 text-muted">
      <CIcon name="cil-user" size="xl" class="mb-2 d-block mx-auto"/>
      <translate translate-context="customer_success.search">No users found</translate>
    </div>

    <!-- Initial state hint -->
    <div v-else class="text-center py-4 text-muted">
      <CIcon name="cil-search" size="xl" class="mb-2 d-block mx-auto"/>
      <translate translate-context="customer_success.search">Start typing to search users</translate>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import debounce from 'lodash/debounce'

export default {
  name: 'SearchUsers',
  data() {
    return {
      form: {
        user_id: '',
        email: '',
        pica_code: '',
      },
      loading: false,
      hasSearched: false
    }
  },
  computed: {
    ...mapGetters('customerSuccess', ['users']),
    ...mapGetters('login', ['hasPerm']),
    canSearchEmail() {
      return this.form.email && this.form.email.length >= 3
    },
    canImpersonate() {
      return this.hasPerm('dashboard.impersonate')
    }
  },
  created() {
    this.debouncedSearch = debounce(this.doSearch, 300)
  },
  beforeDestroy() {
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel()
    }
  },
  methods: {
    ...mapActions('customerSuccess', ['searchUsers']),
    ...mapActions('impersonate', ['impersonate']),

    onEmailInput() {
      if (this.canSearchEmail) {
        this.loading = true
        this.debouncedSearch()
      } else {
        // Cancel pending search and reset state
        this.debouncedSearch.cancel()
        this.loading = false
      }
    },

    async doSearch() {
      try {
        await this.searchUsers({ email: this.form.email })
        this.hasSearched = true
      } catch (error) {
        this.notifyError(this.$pgettext('customer_success.search', 'Search failed'))
      } finally {
        this.loading = false
      }
    },

    async goToUserId() {
      if (!this.form.user_id) return
      this.loading = true
      this.hasSearched = true
      try {
        await this.searchUsers({ user_id: this.form.user_id })
        if (this.users.length === 1) {
          this.openUser(this.users[0])
        } else if (this.users.length === 0) {
          this.notifyError(this.$pgettext('customer_success.search', 'User not found'))
        }
      } finally {
        this.loading = false
      }
    },

    async goToPicaCode() {
      if (!this.form.pica_code) return
      this.loading = true
      this.hasSearched = true
      try {
        await this.searchUsers({ pica_code: this.form.pica_code })
        if (this.users.length === 1) {
          this.openUser(this.users[0])
        } else if (this.users.length === 0) {
          this.notifyError(this.$pgettext('customer_success.search', 'User not found'))
        }
      } finally {
        this.loading = false
      }
    },

    openUser(user) {
      this.$router.push({ name: 'customer_success.user', params: { id: user.id } })
    },

    async onImpersonate(user) {
      await this.impersonate(user)
    }
  }
}
</script>

<style scoped>
.goto-row {
  gap: 1.5rem;
}

.goto-item {
  min-width: 180px;
  max-width: 220px;
}

.input-with-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.input-with-btn .form-control {
  flex: 1;
}

.user-list {
  /* No max-height - show all results */
}

.user-item {
  padding: 1rem;
  border-bottom: 1px solid #ebedef;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background-color: #f8f9fa;
}

.user-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-email {
  font-weight: 500;
  color: #321fdb;
}

.user-id {
  font-size: 0.75rem;
}

.user-actions {
  display: flex;
  align-items: center;
}

.user-badges {
  display: flex;
  align-items: center;
}

.user-checkins {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #ebedef;
}
</style>
